import React, { cloneElement, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';

/**
 * A tooltip that renders via portal to escape overflow:hidden containers.
 * Uses position:fixed so it never affects document flow or causes scrollbar flashes.
 * Same portal pattern as PortalDropdown.
 *
 * @param {Object} props
 * @param {React.ReactElement} props.children - The trigger element (must accept onMouseEnter/onMouseLeave)
 * @param {React.ReactNode} props.content - The tooltip content
 * @param {string} props.placement - "top" or "bottom" (default: "top")
 * @param {boolean} props.showArrow - Whether to show the arrow (default: true)
 */
function PortalTooltip({
 children, content, placement = 'top', showArrow = true,
}) {
  const [hovered, setHovered] = useState(null);

  const handleMouseEnter = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();

    if (placement === 'top') {
      setHovered({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    } else {
      setHovered({
        top: rect.bottom + 8,
        left: rect.left + rect.width / 2,
      });
    }
  };

  const handleMouseLeave = () => setHovered(null);

  const trigger = cloneElement(children, {
    onMouseEnter: (e) => {
      handleMouseEnter(e);
      children.props.onMouseEnter?.(e);
    },
    onMouseLeave: (e) => {
      handleMouseLeave();
      children.props.onMouseLeave?.(e);
    },
    onFocus: (e) => {
      handleMouseEnter(e);
      children.props.onFocus?.(e);
    },
    onBlur: (e) => {
      handleMouseLeave();
      children.props.onBlur?.(e);
    },
  });

  const isTop = placement === 'top';

  const arrow = showArrow && (
    <div
      style={{
        width: 0,
        height: 0,
        borderLeft: '6px solid transparent',
        borderRight: '6px solid transparent',
        ...(isTop ? { borderTop: '6px solid white' } : { borderBottom: '6px solid white' }),
        margin: '0 auto',
        filter: 'drop-shadow(0px 2px 2px #00000012)',
      }}
    />
  );

  return (
    <>
      {trigger}
      {hovered
        && ReactDOM.createPortal(
          <div
            style={{
              position: 'fixed',
              top: hovered.top,
              left: hovered.left,
              transform: isTop ? 'translate(-50%, -100%)' : 'translate(-50%, 0)',
              zIndex: 1060,
              pointerEvents: 'none',
            }}
          >
            {!isTop && arrow}
            <div
              className="inbox-portal-tooltip bg-white rounded-2 text-center paragraph-sm p-3"
              style={{ boxShadow: '0px 0px 6px 0px #00000024' }}
            >
              {content}
            </div>
            {isTop && arrow}
          </div>,
          document.body,
        )}
    </>
  );
}

PortalTooltip.propTypes = {
  children: PropTypes.element.isRequired,
  content: PropTypes.node.isRequired,
  placement: PropTypes.oneOf(['top', 'bottom']),
  showArrow: PropTypes.bool,
};

export default PortalTooltip;
